import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Slide from '@mui/material/Slide';
import Button from '@mui/material/Button';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import ReturnIcon from '@mui/icons-material/KeyboardReturnRounded';
import NavigateNextIcon from '@mui/icons-material/NavigateNextRounded';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Link from '../../UI/Link/Link';
import HomeIcon from '@mui/icons-material/HomeRounded';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import RecordNavigation from '../../General/RecordNavigation';

function HideOnScroll(props) {
  const trigger = useScrollTrigger();
  // While a Joyride tour is active, keep the AppBar pinned. Otherwise the
  // welcome breadcrumb and help-button closeout targets get translated off-
  // screen by the Slide and Joyride can't position around them.
  const tourRunning = useSelector(state => state.help.tourRunning);
  return (
    <Slide appear={false} direction="down" in={tourRunning || !trigger}>
      {props.children}
    </Slide>
  );
}

const BreadcrumbBar = (props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const drawerOpen = useSelector(state => state.navMenu.drawerOpen);
  const drawerWidth = useSelector(state => state.navMenu.drawerWidth);
  const internalAdmin = useSelector(state => state.auth.isInternalAdmin);
  const { goBack, navIds, currentId, basePath, activeVersion } = props;

  const appBar = {
    paddingLeft: { xs: 0, sm: '65px' }, // keep right padding when drawer closed
    //zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  };
  const appBarShift = {
    padding: 0,
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen, }
      ),
  }};

  const authToolbar = (
    <React.Fragment>      
      <HideOnScroll {...props}>
        <AppBar
          variant='breadcrumbs'
          data-guide="welcome"
          sx={drawerOpen ? appBarShift : appBar}
          >
          <Toolbar>
            {location.pathname !== '/home' && location.pathname !== '/admin' && !internalAdmin &&
              <React.Fragment>
                  <Button color="primary" startIcon={<ReturnIcon color="primary" />} data-cy="globalReturnButton"
                          onClick={goBack != null ? () => navigate(goBack) : () => navigate(-1)} sx={{ ml: 1, mr: 2.5 }} >
                      {t('return')}
                  </Button>
                  <Divider orientation='vertical' flexItem={true} variant='middle' sx={{ display: { xs: 'none', sm: 'flex' } }} />
              </React.Fragment>
            }
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} maxItems={3} aria-label="breadcrumb" sx={{ display: { xs: 'none', sm: 'flex' }, ml: 2.5, mr: 'auto' }}>
                {internalAdmin ?
                    <Link
                        underline="hover"
                        sx={{ display: 'flex', alignItems: 'center' }}
                        color="inherit"
                        href="/#/admin"
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        {t('admin')}
                    </Link>
                  :
                    <Link
                        underline="hover"
                        sx={{ display: 'flex', alignItems: 'center' }}
                        color="inherit"
                        href="/#/home"
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        {t('home')}
                    </Link>
                }
                {props.crumbs != null && props.crumbs.length > 0 && props.crumbs.map((crumb, index) => {
                    const resolvedVersion = crumb.path === 'active' && activeVersion != null ? activeVersion : crumb.version;
                    const versionBadge = resolvedVersion != null && (
                        <Typography
                            component='span'
                            sx={{ fontSize: '0.7em', opacity: 0.65, ml: 0.25, verticalAlign: 'sub', fontWeight: 400 }}
                        >
                            .v{resolvedVersion}
                        </Typography>
                    );
                    if(crumb.path === 'active') {
                        return (
                            <Typography
                                sx={{ display: 'flex', alignItems: 'center' }}
                                color="text.primary"
                                key={'breadcrumb-' + index}
                            >
                                {crumb.label}
                                {versionBadge}
                            </Typography>
                        )
                    }else {
                        return (
                            <Link
                                underline="hover"
                                sx={{ display: 'flex', alignItems: 'center' }}
                                color="inherit"
                                href={crumb.path}
                                key={'breadcrumb-' + index}
                            >
                                {crumb.label}
                                {versionBadge}
                            </Link>
                        )
                    }
                })}
            </Breadcrumbs>
            {navIds && navIds.length > 0 &&
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                <RecordNavigation ids={navIds} currentId={currentId} basePath={basePath} t={t} />
              </Box>
            }
            {props.children}
          </Toolbar>
        </AppBar>
      </HideOnScroll>      
    </React.Fragment>    
  );

  return authToolbar;
}

export default BreadcrumbBar;